import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import useSubmitGuard from '../hooks/useSubmitGuard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  FileText, 
  Package, 
  CheckCircle2, 
  Clock, 
  ArrowRightCircle,
  Truck,
  RefreshCw,
  Layers,
  ChevronDown,
  ChevronRight,
  XCircle,
  AlertTriangle,
  Undo2,
  Timer,
  Search,
  ClipboardCheck,
  PackageCheck,
} from 'lucide-react';
import { format } from 'date-fns';
import { ExportButton } from '../utils/exportUtils';
import { offlineDB } from '../utils/offlineDB';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  released: { label: 'Released', color: 'bg-blue-100 text-blue-800', icon: Package },
  received: { label: 'Received', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
  converted_to_po: { label: 'Converted to PO', color: 'bg-purple-100 text-purple-800', icon: ArrowRightCircle },
};

const CONVERT_REASONS = [
  'Stock unavailable',
  'Stock damaged',
  'Insufficient quantity',
  'Quality issue',
  'Other'
];

const TERMINAL_STATUSES = ['received', 'rejected'];

const USAGE_STATUS_CONFIG = {
  reported:         { label: 'Return Pending', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  fully_used:       { label: 'Fully Used',     color: 'bg-green-100 text-green-700 border-green-200' },
  return_confirmed: { label: 'Return Confirmed', color: 'bg-teal-100 text-teal-700 border-teal-200' },
};

const classifyGroup = (group) => {
  const statuses = group.tickets.map(t => t.status);
  if (statuses.every(s => s === 'pending')) return 'new';
  if (statuses.every(s => TERMINAL_STATUSES.includes(s))) return 'completed';
  return 'pending';
};

const VIEW_CONFIG = {
  new:       { label: 'New Requests',       emptyMsg: 'No new requests' },
  pending:   { label: 'Pending Requests',   emptyMsg: 'No pending requests' },
  completed: { label: 'Completed Requests', emptyMsg: 'No completed requests' },
};

const PartsReleasePage = ({ user }) => {
  const guard = useSubmitGuard();
  const location = useLocation();
  const navigate = useNavigate();
  const activeView = location.pathname.endsWith('/pending') ? 'pending'
    : location.pathname.endsWith('/completed') ? 'completed'
    : 'new';
  const [tickets, setTickets] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionFilter, setSectionFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  // Reject dialog
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTicket, setRejectTicket] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Multi-part action dialog
  const [bulkActionOpen, setBulkActionOpen] = useState(false);
  const [bulkGroup, setBulkGroup] = useState(null);
  const [lineActions, setLineActions] = useState({});

  // Return stock
  const [returnModal, setReturnModal] = useState(null);
  const [returnForm, setReturnForm] = useState({ quantity: 1, notes: '' });
  const [submittingReturn, setSubmittingReturn] = useState(false);

  // Mark Usage modal
  const [usageModal, setUsageModal] = useState(null); // { ticket }
  const [usageForm, setUsageForm] = useState({ qty_used: '', notes: '' });
  const [submittingUsage, setSubmittingUsage] = useState(false);

  // Confirm Return modal
  const [confirmReturnModal, setConfirmReturnModal] = useState(null); // ticket
  const [submittingConfirmReturn, setSubmittingConfirmReturn] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [ticketsRes, inventoryRes] = await Promise.all([
        axios.get(`${API}/parts-release`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/inventory`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTickets(ticketsRes.data);
      setInventory(inventoryRes.data);
    } catch (error) {
      toast.error('Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };

  // Map sku_id → available quantity for quick lookup
  const stockMap = useMemo(() => {
    const m = {};
    inventory.forEach(s => { m[s.id] = s.current_quantity; });
    return m;
  }, [inventory]);

  // Return window helpers
  const getReturnWindowEnd = (ticket) => {
    if (!ticket.maintenance_log_completed_at) return null;
    return new Date(new Date(ticket.maintenance_log_completed_at).getTime() + 4 * 60 * 60 * 1000);
  };
  const getTimeRemaining = (windowEnd) => {
    if (!windowEnd) return null;
    const diff = windowEnd - new Date();
    if (diff <= 0) return null;
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  const openReturnModal = (ticket) => {
    const maxReturnable = (ticket.quantity_released || 0) - (ticket.quantity_returned || 0);
    const windowEnd = getReturnWindowEnd(ticket);
    setReturnModal({ ticket, maxReturnable, windowEnd });
    setReturnForm({ quantity: 1, notes: '' });
  };
  const handleReturnStock = async () => {
    if (!returnModal) return;
    setSubmittingReturn(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/parts-release/${returnModal.ticket.id}/return`, null, {
        params: { quantity_to_return: returnForm.quantity, notes: returnForm.notes },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${returnForm.quantity} unit(s) returned to stock successfully`);
      setReturnModal(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Return failed');
    } finally {
      setSubmittingReturn(false);
    }
  };

  const openUsageModal = (ticket) => {
    const qtyReleased = ticket.quantity_released || ticket.quantity_requested || 0;
    setUsageModal({ ticket, qtyReleased });
    setUsageForm({ qty_used: String(qtyReleased), notes: '' });
  };

  const handleMarkUsage = async () => {
    if (!usageModal) return;
    const qtyUsed = parseInt(usageForm.qty_used);
    if (isNaN(qtyUsed) || qtyUsed < 0 || qtyUsed > usageModal.qtyReleased) {
      toast.error(`Qty used must be between 0 and ${usageModal.qtyReleased}`);
      return;
    }
    setSubmittingUsage(true);
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.put(
          `${API}/parts-release/${usageModal.ticket.id}/mark-usage`,
          { qty_used: qtyUsed, notes: usageForm.notes },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(res.data.message || 'Usage recorded');
        setUsageModal(null);
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to record usage');
      } finally {
        setSubmittingUsage(false);
      }
    });
  };

  const handleConfirmReturn = async () => {
    if (!confirmReturnModal) return;
    setSubmittingConfirmReturn(true);
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.put(
          `${API}/parts-release/${confirmReturnModal.id}/confirm-return`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(res.data.message || 'Return confirmed, stock updated');
        setConfirmReturnModal(null);
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to confirm return');
      } finally {
        setSubmittingConfirmReturn(false);
      }
    });
  };

  // Group tickets by maintenance_log_id + door_no
  const allGroups = useMemo(() => {
    const map = {};
    tickets.forEach(t => {
      const key = t.maintenance_log_id || t.id;
      if (!map[key]) {
        map[key] = {
          key,
          maintenance_log_id: t.maintenance_log_id,
          door_no: t.door_no,
          section: t.section,
          created_at: t.created_at,
          tickets: []
        };
      }
      map[key].tickets.push(t);
    });
    return Object.values(map).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [tickets]);

  const groups = useMemo(() => {
    let filtered = allGroups.filter(g => classifyGroup(g) === activeView);
    if (sectionFilter !== 'all') filtered = filtered.filter(g => g.section === sectionFilter);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(g =>
        (g.door_no || '').toLowerCase().includes(q) ||
        g.tickets.some(t =>
          (t.ticket_number || '').toLowerCase().includes(q) ||
          (t.part_name || '').toLowerCase().includes(q) ||
          (t.part_number || '').toLowerCase().includes(q)
        )
      );
    }
    return filtered;
  }, [allGroups, activeView, sectionFilter, searchTerm]);

  const openBulkAction = (group) => {
    const pendingTickets = group.tickets.filter(t => t.status === 'pending');
    if (pendingTickets.length === 0) {
      toast.error('No pending tickets in this group');
      return;
    }
    setBulkGroup({ ...group, tickets: pendingTickets });
    const initial = {};
    pendingTickets.forEach(t => {
      initial[t.id] = { action: 'release', quantity: t.quantity_requested, reason: 'Stock unavailable' };
    });
    setLineActions(initial);
    setBulkActionOpen(true);
  };

  const handleBulkSubmit = async () => {
    const actions = Object.entries(lineActions).map(([ticket_id, v]) => ({
      ticket_id,
      action: v.action,
      quantity: v.quantity,
      reason: v.reason
    }));

    if (!navigator.onLine) {
      try {
        await offlineDB.add({
          module: 'parts_release',
          displayName: `Parts Release – ${bulkGroup?.door_no || 'Unknown'}`,
          endpoint: '/parts-release/bulk-action',
          method: 'POST',
          data: actions,
        });
        const count = await offlineDB.pendingCount();
        if (window.__setOfflinePendingCount) window.__setOfflinePendingCount(count);
        toast.success('Saved locally — will sync when back online', {
          description: 'Parts release stored on this device',
          duration: 4000,
        });
        setBulkActionOpen(false);
        setBulkGroup(null);
      } catch (_e) {
        toast.error('Failed to save offline. Storage may be full.');
      }
      return;
    }

    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(`${API}/parts-release/bulk-action`, actions, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const results = res.data.results || [];
        const released = results.filter(r => r.action === 'released').length;
        const converted = results.filter(r => r.action === 'converted_to_po').length;
        const failed = results.filter(r => !r.success);
        if (failed.length > 0) {
          failed.forEach(f => toast.error(f.error || 'Failed for ticket ' + f.ticket_id));
        }
        if (released > 0 || converted > 0)
          toast.success(`Done: ${released} released, ${converted} converted to PO`);
        setBulkActionOpen(false);
        setBulkGroup(null);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to process actions');
      }
    });
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Please enter a rejection reason'); return; }
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/parts-release/${rejectTicket.id}/reject?reason=${encodeURIComponent(rejectReason)}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ticket rejected');
        setRejectOpen(false);
        setRejectTicket(null);
        setRejectReason('');
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to reject ticket');
      }
    });
  };

  const handleApprove = async (ticket) => {
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API}/parts-release/${ticket.id}/release?quantity_released=${ticket.quantity_requested}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Approved & released: ${ticket.part_name} x${ticket.quantity_requested}`);
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to approve ticket');
      }
    });
  };

  const handleReceive = async (ticketId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/parts-release/${ticketId}/receive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Receipt confirmed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm receipt');
    }
  };

  const toggleGroup = (key) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  const getGroupStatus = (group) => {
    const statuses = [...new Set(group.tickets.map(t => t.status))];
    if (statuses.length === 1) return statuses[0];
    if (statuses.includes('pending')) return 'pending';
    return 'mixed';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{VIEW_CONFIG[activeView].label}</h1>
          <p className="text-slate-600 mt-1">Parts release requests from maintenance</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            rows={tickets}
            columns={[
              { header: 'Ticket #', key: 'ticket_number' },
              { header: 'Asset', key: 'door_no' },
              { header: 'Section', key: 'section' },
              { header: 'Part Name', key: 'part_name' },
              { header: 'Part Number', key: 'part_number' },
              { header: 'Qty Requested', key: 'quantity_requested' },
              { header: 'Qty Released', key: 'quantity_released' },
              { header: 'Status', key: 'status' },
              { header: 'Released By', key: 'released_by' },
              { header: 'Date', key: r => r.created_at?.slice(0, 10) },
            ]}
            filename={`parts_release_${new Date().toISOString().slice(0,10)}`}
            title="Parts Release Tickets"
            subtitle={`Exported on ${new Date().toLocaleDateString('en-IN')}`}
          />
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards — view-based counts */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { view: 'new', label: 'New Requests', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
          { view: 'pending', label: 'Pending Requests', color: 'bg-blue-100 text-blue-800', icon: Package },
          { view: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
        ].map(({ view, label, color, icon: Icon }) => {
          const count = allGroups.filter(g => classifyGroup(g) === view).length;
          return (
            <Card key={view} className={`cursor-pointer transition-all hover:shadow-md ${activeView === view ? 'ring-2 ring-slate-900' : ''}`}
              onClick={() => navigate('/inventory/parts-release/' + view)}
              data-testid={`store-stat-${view}`}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">{label}</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                  <div className={`p-2 rounded-full ${color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <Select value={sectionFilter} onValueChange={setSectionFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by section" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sections</SelectItem>
            <SelectItem value="truck">Truck Section</SelectItem>
            <SelectItem value="machinery">Machinery Section</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by door no, ticket #, part name or part number..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="parts-release-search-input"
          />
        </div>
      </div>

      {/* Grouped Tickets */}
      <div className="space-y-3">
        {groups.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center">
            <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">{VIEW_CONFIG[activeView].emptyMsg}</p>
          </div>
        ) : (
          groups.map(group => {
            const pendingCount = group.tickets.filter(t => t.status === 'pending').length;
            const groupStatus = getGroupStatus(group);
            const isExpanded = expandedGroups[group.key];

            return (
              <div key={group.key} className="bg-white rounded-lg border shadow-sm overflow-hidden">
                {/* Group Header */}
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => toggleGroup(group.key)}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">{group.door_no}</span>
                        <Badge variant="outline" className="capitalize text-xs">
                          <Truck className="h-3 w-3 mr-1" />
                          {group.section}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          {group.created_at ? format(new Date(group.created_at), 'MMM d, HH:mm') : ''}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {group.tickets.length} part{group.tickets.length > 1 ? 's' : ''} •{' '}
                        {pendingCount > 0 && <span className="text-yellow-600 font-medium">{pendingCount} pending</span>}
                        {pendingCount === 0 && <span className="text-green-600">all processed</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {pendingCount > 0 && (
                      <Button
                        size="sm"
                        className="bg-slate-900 hover:bg-slate-800"
                        onClick={(e) => { e.stopPropagation(); openBulkAction(group); }}
                        data-testid={`process-group-${group.key}`}
                      >
                        <Layers className="h-3.5 w-3.5 mr-1.5" />
                        Process {pendingCount} Part{pendingCount > 1 ? 's' : ''}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Expanded — Ticket Cards */}
                {isExpanded && (
                  <div className="border-t bg-slate-50/50 p-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {group.tickets.map(ticket => {
                      const sc = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.pending;
                      const StatusIcon = sc.icon;
                      const inStock = ticket.sku_id ? (stockMap[ticket.sku_id] ?? null) : null;
                      const stockLow = inStock !== null && inStock < ticket.quantity_requested;

                      return (
                        <div
                          key={ticket.id}
                          className={`bg-white rounded-lg border-2 p-4 flex flex-col gap-3 shadow-sm transition-colors
                            ${ticket.status === 'pending' ? 'border-yellow-200' : ''}
                            ${ticket.status === 'released' ? 'border-blue-200' : ''}
                            ${ticket.status === 'received' ? 'border-green-200' : ''}
                            ${ticket.status === 'rejected' ? 'border-red-200' : ''}
                            ${ticket.status === 'converted_to_po' ? 'border-purple-200' : ''}
                          `}
                          data-testid={`ticket-card-${ticket.id}`}
                        >
                          {/* Card header */}
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-mono text-xs text-slate-400">{ticket.ticket_number}</p>
                              <p className="font-semibold text-slate-900 text-sm mt-0.5">{ticket.part_name}</p>
                              {ticket.part_number && <p className="text-xs text-slate-400">{ticket.part_number}</p>}
                            </div>
                            <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${sc.color}`}>
                              <StatusIcon className="h-3 w-3" />
                              {sc.label}
                            </span>
                          </div>

                          {/* Qty row */}
                          <div className={`grid gap-2 text-center ${ticket.quantity_returned > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                            <div className="bg-slate-50 rounded-md py-1.5">
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Requested</p>
                              <p className="font-bold text-slate-800">{ticket.quantity_requested}</p>
                            </div>
                            <div className={`rounded-md py-1.5 ${inStock !== null ? (stockLow ? 'bg-red-50' : 'bg-green-50') : 'bg-slate-50'}`}>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">In Stock</p>
                              <p className={`font-bold flex items-center justify-center gap-0.5 ${stockLow ? 'text-red-600' : 'text-green-700'}`}>
                                {inStock !== null ? inStock : '—'}
                                {stockLow && <AlertTriangle className="h-3 w-3" />}
                              </p>
                            </div>
                            <div className="bg-slate-50 rounded-md py-1.5">
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Released</p>
                              <p className="font-bold text-slate-800">{ticket.quantity_released ?? '—'}</p>
                            </div>
                            {ticket.quantity_returned > 0 && (
                              <div className="bg-teal-50 rounded-md py-1.5">
                                <p className="text-[10px] text-teal-600 uppercase tracking-wide">Returned</p>
                                <p className="font-bold text-teal-700">{ticket.quantity_returned}</p>
                              </div>
                            )}
                          </div>

                          {/* Rejection reason */}
                          {ticket.rejection_reason && (
                            <div className="bg-red-50 border border-red-100 rounded-md px-2.5 py-1.5 text-xs text-red-700">
                              Rejection: {ticket.rejection_reason}
                            </div>
                          )}

                          {/* Usage status badge */}
                          {ticket.status === 'received' && ticket.usage_status && (() => {
                            const uc = USAGE_STATUS_CONFIG[ticket.usage_status];
                            if (!uc) return null;
                            return (
                              <div className={`rounded-md px-2.5 py-1.5 text-xs font-medium border flex items-center gap-1.5 ${uc.color}`}>
                                <ClipboardCheck className="h-3 w-3 flex-shrink-0" />
                                {uc.label}
                                {ticket.usage_status === 'reported' && ticket.qty_to_return > 0 &&
                                  <span className="ml-auto font-bold">{ticket.qty_to_return} to return</span>}
                                {ticket.usage_status === 'fully_used' &&
                                  <span className="ml-auto">{ticket.qty_used} used</span>}
                                {ticket.usage_status === 'return_confirmed' &&
                                  <span className="ml-auto">{ticket.qty_to_return} returned</span>}
                              </div>
                            );
                          })()}

                          {/* Actions */}
                          <div className="flex flex-col gap-2 mt-auto pt-1">
                            {ticket.status === 'released' && (
                              <Button
                                size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-xs h-7"
                                onClick={() => handleReceive(ticket.id)}
                                data-testid={`receive-ticket-${ticket.id}`}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" />Confirm Receipt
                              </Button>
                            )}
                            {ticket.status === 'pending' && (
                              <div className="flex gap-2 mt-2">
                                <Button
                                  size="sm" variant="outline"
                                  className="flex-1 border-emerald-400 text-emerald-700 hover:bg-emerald-50 text-xs h-7"
                                  onClick={() => handleApprove(ticket)}
                                  data-testid={`approve-ticket-${ticket.id}`}
                                >
                                  <CheckCircle2 className="h-3 w-3 mr-1" />Approve
                                </Button>
                                <Button
                                  size="sm" variant="outline"
                                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50 text-xs h-7"
                                  onClick={() => { setRejectTicket(ticket); setRejectOpen(true); }}
                                  data-testid={`reject-ticket-${ticket.id}`}
                                >
                                  <XCircle className="h-3 w-3 mr-1" />Reject
                                </Button>
                              </div>
                            )}

                            {/* Mark Usage — received tickets, no usage yet, ticket creator or dept head */}
                            {ticket.status === 'received' && !ticket.usage_status && (() => {
                              const canMarkUsage = ticket.created_by === user?.email ||
                                user?.is_admin || user?.is_super_admin ||
                                user?.permissions?.can_manage_users;
                              if (!canMarkUsage) return null;
                              return (
                                <Button
                                  size="sm" variant="outline"
                                  className="text-xs h-7 border-blue-400 text-blue-700 hover:bg-blue-50"
                                  onClick={() => openUsageModal(ticket)}
                                  data-testid={`mark-usage-btn-${ticket.id}`}
                                >
                                  <ClipboardCheck className="h-3 w-3 mr-1" />
                                  Mark Usage
                                </Button>
                              );
                            })()}

                            {/* Confirm Return — stores/admin, when usage reported with parts to return */}
                            {ticket.status === 'received' && ticket.usage_status === 'reported' && ticket.qty_to_return > 0 && (() => {
                              const canConfirm = user?.is_admin || user?.is_super_admin ||
                                user?.permissions?.parts_release?.edit;
                              if (!canConfirm) return null;
                              return (
                                <Button
                                  size="sm"
                                  className="text-xs h-7 bg-teal-600 hover:bg-teal-700 text-white"
                                  onClick={() => setConfirmReturnModal(ticket)}
                                  data-testid={`confirm-return-btn-${ticket.id}`}
                                >
                                  <PackageCheck className="h-3 w-3 mr-1" />
                                  Confirm Return ({ticket.qty_to_return})
                                </Button>
                              );
                            })()}

                            {/* Return Stock — received tickets only (legacy flow) */}
                            {ticket.status === 'received' && !ticket.usage_status && (() => {
                              const maxRet = (ticket.quantity_released || 0) - (ticket.quantity_returned || 0);
                              if (maxRet <= 0) return null;
                              const canReturn = user?.is_admin || user?.is_super_admin ||
                                user?.permissions?.parts_release?.edit;
                              if (!canReturn) return null;
                              return (
                                <Button
                                  size="sm" variant="outline"
                                  className="text-xs h-7 border-teal-400 text-teal-700 hover:bg-teal-50"
                                  onClick={() => openReturnModal(ticket)}
                                  data-testid={`return-stock-btn-${ticket.id}`}
                                >
                                  <Undo2 className="h-3 w-3 mr-1" />
                                  Return Stock ({maxRet})
                                </Button>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Multi-Part Action Dialog */}
      <Dialog open={bulkActionOpen} onOpenChange={setBulkActionOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Process Parts — {bulkGroup?.door_no}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2 mb-4">
            For each part, choose to <strong>Release from Store</strong> or <strong>Convert to PO</strong> (send to HO for purchase)
          </p>

          <div className="space-y-3">
            {bulkGroup?.tickets.map(ticket => {
              const la = lineActions[ticket.id] || { action: 'release', quantity: ticket.quantity_requested, reason: 'Stock unavailable' };
              const inStock = ticket.sku_id ? (stockMap[ticket.sku_id] ?? '—') : '—';
              const stockLow = typeof inStock === 'number' && inStock < la.quantity;
              return (
                <div key={ticket.id} className={`border rounded-lg p-4 transition-colors ${la.action === 'release' ? 'border-blue-200 bg-blue-50/30' : 'border-purple-200 bg-purple-50/30'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{ticket.part_name}</p>
                      {ticket.part_number && <p className="text-xs text-slate-500">{ticket.part_number}</p>}
                      <p className="text-xs text-slate-500 mt-0.5">Ticket: {ticket.ticket_number}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => setLineActions(p => ({ ...p, [ticket.id]: { ...la, action: 'release' } }))}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${la.action === 'release' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'}`}
                        data-testid={`action-release-${ticket.id}`}
                      >
                        Release from Store
                      </button>
                      <button
                        onClick={() => setLineActions(p => ({ ...p, [ticket.id]: { ...la, action: 'convert_to_po' } }))}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${la.action === 'convert_to_po' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-slate-600 border-slate-300 hover:border-purple-400'}`}
                        data-testid={`action-convert-${ticket.id}`}
                      >
                        Convert to PO
                      </button>
                    </div>
                  </div>

                  {/* Stock availability indicator */}
                  {ticket.sku_id && (
                    <div className={`mt-2 flex items-center gap-1.5 text-xs font-medium ${stockLow ? 'text-red-600' : 'text-green-700'}`}>
                      {stockLow
                        ? <><AlertTriangle className="h-3.5 w-3.5" /> Only {inStock} in stock — requested {la.quantity}</>
                        : <><Package className="h-3.5 w-3.5" /> {inStock} available in stock</>}
                    </div>
                  )}

                  <div className="mt-3 flex items-center gap-4">
                    <div>
                      <Label className="text-xs">Qty ({la.action === 'release' ? 'to release' : 'requested'})</Label>
                      <Input
                        type="number"
                        min="1"
                        max={ticket.quantity_requested}
                        value={la.quantity}
                        onChange={(e) => setLineActions(p => ({ ...p, [ticket.id]: { ...la, quantity: parseInt(e.target.value) || 1 } }))}
                        className="mt-1 h-8 w-24"
                      />
                    </div>
                    {la.action === 'convert_to_po' && (
                      <div className="flex-1">
                        <Label className="text-xs">Reason for PO</Label>
                        <Select value={la.reason} onValueChange={(val) => setLineActions(p => ({ ...p, [ticket.id]: { ...la, reason: val } }))}>
                          <SelectTrigger className="mt-1 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CONVERT_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="mt-4 p-3 bg-slate-50 rounded-lg border">
            <p className="text-sm font-medium text-slate-700">Summary:</p>
            <div className="flex gap-4 mt-1">
              <span className="text-sm text-blue-700">
                {Object.values(lineActions).filter(a => a.action === 'release').length} parts to release
              </span>
              <span className="text-sm text-purple-700">
                {Object.values(lineActions).filter(a => a.action === 'convert_to_po').length} parts to PO
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setBulkActionOpen(false)}>Cancel</Button>
            <Button
              onClick={handleBulkSubmit}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="bulk-action-submit"
            >
              Confirm Actions
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Ticket Dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700">Reject Parts Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-md bg-slate-50 p-3 text-sm">
              <p className="font-medium">{rejectTicket?.part_name}</p>
              <p className="text-slate-500 text-xs mt-0.5">Ticket: {rejectTicket?.ticket_number} • Qty: {rejectTicket?.quantity_requested}</p>
            </div>
            <div>
              <Label>Rejection Reason *</Label>
              <Textarea
                className="mt-1.5"
                rows={3}
                placeholder="e.g. Part number mismatch, wrong specification..."
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setRejectOpen(false); setRejectReason(''); }}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleReject}
              data-testid="confirm-reject-btn"
            >
              Confirm Rejection
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return Stock Modal */}
      <Dialog open={!!returnModal} onOpenChange={() => setReturnModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="h-5 w-5 text-teal-600" />
              Return Stock to Store
            </DialogTitle>
          </DialogHeader>
          {returnModal && (
            <div className="space-y-4 py-1">
              {/* Part summary */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <p className="font-semibold text-sm text-slate-800">{returnModal.ticket.part_name}</p>
                <p className="text-xs text-slate-500">{returnModal.ticket.ticket_number} · {returnModal.ticket.door_no}</p>
                <div className="flex gap-4 pt-1 text-xs">
                  <span className="text-slate-500">Released: <strong className="text-slate-800">{returnModal.ticket.quantity_released}</strong></span>
                  {returnModal.ticket.quantity_returned > 0 && (
                    <span className="text-teal-600">Already returned: <strong>{returnModal.ticket.quantity_returned}</strong></span>
                  )}
                  <span className="text-teal-700 font-medium">Returnable: <strong>{returnModal.maxReturnable}</strong></span>
                </div>
              </div>

              {/* Return window */}
              {returnModal.windowEnd && (
                <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <Timer className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                  <span className="text-amber-700">
                    Window closes at <strong>{returnModal.windowEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>
                    {getTimeRemaining(returnModal.windowEnd) && ` (${getTimeRemaining(returnModal.windowEnd)} remaining)`}
                  </span>
                </div>
              )}

              {/* Quantity */}
              <div>
                <Label className="text-sm font-medium">Quantity to Return</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <Button variant="outline" size="sm" className="h-9 w-9 p-0"
                    onClick={() => setReturnForm(f => ({ ...f, quantity: Math.max(1, f.quantity - 1) }))}>−</Button>
                  <input
                    type="number" min={1} max={returnModal.maxReturnable}
                    value={returnForm.quantity}
                    onChange={e => setReturnForm(f => ({ ...f, quantity: Math.max(1, Math.min(returnModal.maxReturnable, parseInt(e.target.value) || 1)) }))}
                    className="w-20 text-center border rounded-md h-9 text-sm font-semibold"
                    data-testid="return-quantity-input"
                  />
                  <Button variant="outline" size="sm" className="h-9 w-9 p-0"
                    onClick={() => setReturnForm(f => ({ ...f, quantity: Math.min(returnModal.maxReturnable, f.quantity + 1) }))}>+</Button>
                  <span className="text-xs text-slate-400">of {returnModal.maxReturnable}</span>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-sm font-medium">Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Textarea
                  className="mt-1.5 text-sm" rows={2}
                  placeholder="e.g. Job completed early, 1 unused unit"
                  value={returnForm.notes}
                  onChange={e => setReturnForm(f => ({ ...f, notes: e.target.value }))}
                  data-testid="return-notes-input"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setReturnModal(null)}>Cancel</Button>
                <Button
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  onClick={handleReturnStock}
                  disabled={submittingReturn}
                  data-testid="confirm-return-btn"
                >
                  {submittingReturn ? 'Returning...' : `Return ${returnForm.quantity} Unit${returnForm.quantity > 1 ? 's' : ''}`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Mark Usage Modal */}
      <Dialog open={!!usageModal} onOpenChange={() => setUsageModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-blue-600" />
              Mark Parts Usage
            </DialogTitle>
          </DialogHeader>
          {usageModal && (
            <div className="space-y-4 py-1">
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <p className="font-semibold text-sm text-slate-800">{usageModal.ticket.part_name}</p>
                <p className="text-xs text-slate-500">{usageModal.ticket.ticket_number} · {usageModal.ticket.door_no}</p>
                <p className="text-xs text-slate-600 mt-1">
                  <span className="font-medium">{usageModal.qtyReleased}</span> unit{usageModal.qtyReleased > 1 ? 's' : ''} were issued to you
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium">How many did you use?</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <Button variant="outline" size="sm" className="h-9 w-9 p-0"
                    onClick={() => setUsageForm(f => ({ ...f, qty_used: String(Math.max(0, parseInt(f.qty_used || 0) - 1)) }))}>−</Button>
                  <input
                    type="number" min={0} max={usageModal.qtyReleased}
                    value={usageForm.qty_used}
                    onChange={e => setUsageForm(f => ({ ...f, qty_used: e.target.value }))}
                    className="w-20 text-center border rounded-md h-9 text-sm font-semibold"
                    data-testid="usage-qty-used-input"
                  />
                  <Button variant="outline" size="sm" className="h-9 w-9 p-0"
                    onClick={() => setUsageForm(f => ({ ...f, qty_used: String(Math.min(usageModal.qtyReleased, parseInt(f.qty_used || 0) + 1)) }))}>+</Button>
                  <span className="text-xs text-slate-400">of {usageModal.qtyReleased}</span>
                </div>
                {(() => {
                  const used = parseInt(usageForm.qty_used);
                  const toReturn = usageModal.qtyReleased - (isNaN(used) ? 0 : used);
                  if (toReturn > 0) return (
                    <p className="text-xs text-orange-600 mt-1.5 font-medium">
                      {toReturn} unit{toReturn > 1 ? 's' : ''} will be marked for return to stores
                    </p>
                  );
                  if (!isNaN(used) && used === usageModal.qtyReleased) return (
                    <p className="text-xs text-green-600 mt-1.5 font-medium">All parts used — nothing to return</p>
                  );
                  return null;
                })()}
              </div>

              <div>
                <Label className="text-sm font-medium">Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Textarea
                  className="mt-1.5 text-sm" rows={2}
                  placeholder="e.g. Shift ended, 2 units unused..."
                  value={usageForm.notes}
                  onChange={e => setUsageForm(f => ({ ...f, notes: e.target.value }))}
                  data-testid="usage-notes-input"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setUsageModal(null)}>Cancel</Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={handleMarkUsage}
                  disabled={submittingUsage}
                  data-testid="submit-usage-btn"
                >
                  {submittingUsage ? 'Saving...' : 'Submit Usage'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Return Modal */}
      <Dialog open={!!confirmReturnModal} onOpenChange={() => setConfirmReturnModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageCheck className="h-5 w-5 text-teal-600" />
              Confirm Parts Return
            </DialogTitle>
          </DialogHeader>
          {confirmReturnModal && (
            <div className="space-y-4 py-1">
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <p className="font-semibold text-sm text-slate-800">{confirmReturnModal.part_name}</p>
                <p className="text-xs text-slate-500">{confirmReturnModal.ticket_number} · {confirmReturnModal.door_no}</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
                <p className="font-semibold">Returning {confirmReturnModal.qty_to_return} unit{confirmReturnModal.qty_to_return > 1 ? 's' : ''}</p>
                <p className="text-xs mt-0.5">Reported by: {(confirmReturnModal.usage_reported_by || '').split('@')[0]}</p>
                {confirmReturnModal.usage_notes && <p className="text-xs mt-0.5 italic">"{confirmReturnModal.usage_notes}"</p>}
              </div>
              <p className="text-sm text-slate-600">
                Confirming will add <strong>{confirmReturnModal.qty_to_return} unit{confirmReturnModal.qty_to_return > 1 ? 's' : ''}</strong> of <strong>{confirmReturnModal.part_name}</strong> back into stock.
              </p>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setConfirmReturnModal(null)}>Cancel</Button>
                <Button
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  onClick={handleConfirmReturn}
                  disabled={submittingConfirmReturn}
                  data-testid="submit-confirm-return-btn"
                >
                  {submittingConfirmReturn ? 'Confirming...' : 'Confirm & Update Stock'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartsReleasePage;
